import { Component, Input, OnChanges, forwardRef } from '@angular/core';
import { SimpleChanges } from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormBuilder,
  UntypedFormGroup,
  UntypedFormArray,
  NG_VALUE_ACCESSOR,
  NG_ASYNC_VALIDATORS,
  AbstractControl,
  AsyncValidator,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { XenialService } from '@services/xenial/1.0/xenial.service';
import { ButtonStyle } from '@constants/button/1.0';
import { StoreService } from '@services/store/1.0/store.service';
import { MatDialog } from '@angular/material/dialog';
import { ManageStoresComponent } from '@app/users/users/edit-user/manage-stores/manage-stores.component';
import { forkJoin, of } from 'rxjs';
import { MessageType } from '@constants/message/1.0';
import { NgIf, NgClass } from '@angular/common';
import { MessageComponent } from '../../message/1.0/message.component';
import { ButtonComponent } from '../../button/1.0/button.component';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatFormField, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'txp-catalog-xenial-stores',
  templateUrl: './catalog-xenial-stores.component.html',
  styleUrls: ['./catalog-xenial-stores.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CatalogXenialStoresComponent),
      multi: true,
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => CatalogXenialStoresComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    NgIf,
    MessageComponent,
    ButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatFormField,
    MatInput,
    NgClass,
    MatError,
    MatSlideToggle,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    TranslateModule,
  ],
})
export class CatalogXenialStoresComponent
  implements OnChanges, AsyncValidator, ControlValueAccessor
{
  @Input() formSubmitted: boolean = false;
  @Input() dataGroupId: string;
  @Input() catalogEntryId: string;
  @Input() brandId: string;
  @Input() entityType: 'PRODUCT' | 'MODIFIER' = 'PRODUCT';
  @Input() instructionsList: any[] = [];
  @Input() rawPosValue: string = '';

  showNoPriceMsg = false;
  showNoDataGroupIdMsg = false;
  allStores: any[] = [];
  form: UntypedFormGroup;
  onChangeFn: any;
  onTouchedFn: any;
  buttonStyle = ButtonStyle;
  messageType = MessageType;
  isLoading = false;
  manageStoresComponent = ManageStoresComponent;
  showNoStoresMsg = false;
  showNoAvailabilityMsg = false;
  showNoPosAssociationWarning = false;
  xenialInstructionId: string | null = null;

  stores: any[] = [];
  displayedColumns: string[] = ['storeNumber', 'name', 'price', 'available'];

  constructor(
    private xenialService: XenialService,
    private formBuilder: UntypedFormBuilder,
    private storeService: StoreService,
    private dialog: MatDialog,
  ) {
    this.form = this.formBuilder.group({
      stores: this.formBuilder.array([]), // FormArray of pricing objects
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Show warning if dataGroupId is blank or null
    if (!this.dataGroupId || this.dataGroupId.trim() === '') {
      this.showNoDataGroupIdMsg = true;
    } else {
      this.showNoDataGroupIdMsg = false;
    }
    // Always mark all controls as touched when formSubmitted is true
    if (this.formSubmitted && this.storesFormArray) {
      this.storesFormArray.controls.forEach((group: UntypedFormGroup) => {
        const priceControl = group.get('price');
        if (priceControl) {
          priceControl.markAsTouched();
          priceControl.updateValueAndValidity();
        }
      });
    }
    // Show warning if POS association is missing for MODIFIER
    if (
      this.entityType === 'MODIFIER' &&
      (changes.instructionsList || changes.catalogEntryId || changes.rawPosValue) &&
      this.instructionsList &&
      this.catalogEntryId &&
      this.rawPosValue
    ) {
      this.findExistingInstructionId();
      this.showNoPosAssociationWarning = !!this.xenialInstructionId;
    }
    // Reset form and stores if switching to a new product or one without prices
    if (changes.catalogEntryId || changes.brandId) {
      this.resetStoresForm();
    }
    if ((changes.brandId || changes.catalogEntryId) && this.brandId && this.catalogEntryId) {
      this.populateXenialStores();
    }
  }

  resetStoresForm(): void {
    this.stores = [];
    this.form.setControl('stores', this.formBuilder.array([]));
    this.showNoStoresMsg = false;
    this.showNoPriceMsg = false;
    this.showNoAvailabilityMsg = false;
  }

  get storesFormArray(): UntypedFormArray {
    return this.form.get('stores') as UntypedFormArray;
  }

  populateXenialStores(): void {
    // Always reset before populating
    this.resetStoresForm();
    if (this.catalogEntryId && this.brandId) {
      // Use forkJoin to fetch store prices, all stores, availabilities, and xenial data group
      forkJoin({
        storePrices: this.xenialService.getStorePrices(this.brandId, this.catalogEntryId),
        allStores: this.storeService.get(this.brandId),
        availabilities: this.xenialService.getAvailabilitiesForCatalogEntry(
          this.brandId,
          this.catalogEntryId,
        ),
        xenialDataGroupId: this.xenialService.findXenialDataGroup(this.brandId),
      }).subscribe({
        next: ({ storePrices, allStores, availabilities, xenialDataGroupId }) => {
          this.allStores = allStores;

          // Filter storePrices by xenialDataGroupId if available
          let filteredStorePrices = storePrices;
          if (xenialDataGroupId && Array.isArray(storePrices)) {
            filteredStorePrices = storePrices.filter((store: any) => {
              // Filter stores that match the xenial data group ID
              const storeId = store.id || store.storeId;
              const fullStore = allStores.find(
                (s: any) => s.id === storeId || s.storeId === storeId,
              );
              return fullStore?.dataGroupId === xenialDataGroupId;
            });
          }

          // Map storeId to availability boolean
          const availabilityMap: Record<string, boolean> = {};
          if (Array.isArray(availabilities)) {
            availabilities.forEach((a: any) => {
              if (a.storeId) {
                availabilityMap[a.storeId] = !!a.available;
              }
            });
          }
          if (!Array.isArray(filteredStorePrices) || filteredStorePrices.length === 0) {
            this.showNoStoresMsg = true;
            this.stores = [];
            this.form.setControl('stores', this.formBuilder.array([]));
            return;
          }
          this.stores = filteredStorePrices.map((store) => {
            let priceValue = store.price;
            if (priceValue === undefined || priceValue === null || isNaN(Number(priceValue))) {
              priceValue = null;
            } else {
              priceValue = Number(priceValue);
            }
            // Map storeNumber and name from allStores if missing
            const storeId = store.id || store.storeId;
            const fullStore = allStores.find((s: any) => s.id === storeId || s.storeId === storeId);
            return {
              storeId,
              storeNumber: store.storeNumber ?? (fullStore ? fullStore.storeNumber : undefined),
              catalogEntryId: this.catalogEntryId,
              entityType: this.entityType,
              dataGroupId: this.dataGroupId || '',
              price: priceValue,
              defaultPrice: null,
              name: store.name ?? (fullStore ? fullStore.name : undefined),
              available: availabilityMap[storeId] ?? true,
            };
          });
          // Patch FormArray
          const storesFormArray = this.formBuilder.array([]);
          this.stores.forEach((store) => {
            storesFormArray.push(this.createStoreFormGroup(store));
          });
          this.form.setControl('stores', storesFormArray);
          if (this.onChangeFn) {
            this.registerOnChange(this.onChangeFn);
          }
          this.showNoPriceMsg = false;
          this.showNoAvailabilityMsg = false;
          this.showNoStoresMsg = false;
        },
        error: () => {
          this.showNoStoresMsg = true;
          this.showNoPriceMsg = true;
          this.showNoAvailabilityMsg = true;
          this.form.setControl('stores', this.formBuilder.array([]));
        },
      });
    }
  }

  /**
   * Finds the instruction object in instructionsList matching the given criteria.
   * Returns the instruction object or null if not found.
   */
  findExistingInstructionId(): any | null {
    if (!this.instructionsList || !this.catalogEntryId || !this.rawPosValue || !this.dataGroupId) {
      return null;
    }

    let xenialInstruction =
      this.instructionsList.find(
        (inst) =>
          inst.associatedCatalogEntryId === this.catalogEntryId &&
          inst.dataGroupId === this.dataGroupId &&
          inst.rawPosValue === this.rawPosValue,
      ) || null;

    this.xenialInstructionId = xenialInstruction?.catalogEntryId;

    return xenialInstruction?.catalogEntryId;
  }

  /**
   * Helper to create a FormGroup for a store object
   */
  createStoreFormGroup(store: any): UntypedFormGroup {
    return this.formBuilder.group({
      storeId: [store.storeId],
      storeNumber: [store.storeNumber],
      catalogEntryId: [store.catalogEntryId],
      entityType: [store.entityType],
      dataGroupId: [store.dataGroupId],
      price: [store.price, [Validators.required]],
      defaultPrice: [Number(store.defaultPrice)],
      name: [store.name],
      available: [typeof store.available === 'boolean' ? store.available : true],
    });
  }

  onAddStore(): void {
    this.isLoading = false;
    const selectedStoreIds = this.storesFormArray.getRawValue().map((s: any) => s.storeId);
    const dialogRef = this.dialog.open(this.manageStoresComponent, {
      data: { stores: this.allStores, selectedStoreIds },
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && result.selectedStoreList) {
        // Build new FormArray with only the selected stores
        const storesFormArray = this.formBuilder.array([]);
        result.selectedStoreList.forEach((store: any) => {
          const patchedStore = {
            ...store,
            storeId: store.id || store.storeId, // GUID only
            storeNumber: store.storeNumber, // Numeric store number
            catalogEntryId: this.catalogEntryId,
            entityType: this.entityType,
            dataGroupId: this.dataGroupId || '',
            price:
              store.price === undefined || store.price === null || isNaN(Number(store.price))
                ? null
                : Number(store.price),
            defaultPrice: null,
            name: store.name,
            available: true,
          };
          storesFormArray.push(this.createStoreFormGroup(patchedStore));
        });
        this.form.setControl('stores', storesFormArray);
        storesFormArray.updateValueAndValidity();
        if (this.onChangeFn) {
          this.registerOnChange(this.onChangeFn);
        }
      }
    });
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  // ControlValueAccessor methods
  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
    // Subscribe to value changes for each FormGroup in FormArray
    const storesFormArray = this.form.get('stores') as UntypedFormArray;
    if (storesFormArray) {
      storesFormArray.controls.forEach((group: UntypedFormGroup) => {
        group.valueChanges.subscribe(() => {
          if (this.onChangeFn) {
            const filtered = storesFormArray.getRawValue().map((store: any) => {
              const obj: any = {
                storeId: store.storeId,
                catalogEntryId: store.catalogEntryId,
                entityType: this.entityType,
                dataGroupId: this.dataGroupId || '',
                price: store.price,
                available: store.available,
              };
              if (this.entityType === 'MODIFIER') {
                obj.instructionId = this.xenialInstructionId;
              }
              return obj;
            });
            this.onChangeFn(filtered);
          }
        });
      });
    }
  }

  writeValue(obj: any): void {}

  validate(control: AbstractControl) {
    // Always return an Observable for async validator
    return of(this.form.valid ? null : { invalidForm: true });
  }
}
